// Consent-gated GA4 conversion events.
//
// index.html always defines a stub `gtag` that pushes into `dataLayer`, so a
// plain `typeof window.gtag === 'function'` check is not a consent check: an
// event queued before consent would be replayed once gtag.js loads after a
// later opt-in. Every event therefore requires (1) stored analytics consent,
// (2) the Google tags configured by AnalyticsLoader after that consent, and
// (3) no Firebase auth-action query in the URL. Events always carry
// `send_to: G-79JNFWTLCJ` so they never fan out to the Google Ads tag.
//
// Privacy contract (docs/seo/2026-10-18_ga4-conversion-measurement.md): no
// user id, email, phone, file name, error message, URL or query string is
// ever sent as an event parameter. Only fixed enums, coarse buckets, package
// ids, Paddle transaction ids and amounts leave the browser.
//
// This module must stay free of Firebase imports: ToolLandingPage and the
// main bundle load it on public landing pages.
import { isAnalyticsConsentGranted } from './cookiePreferences';
import { getPublicRouteTemplate, PUBLIC_LOCALE_PREFIXES } from './webVitals.mjs';
import { isAuthActionUrlActive } from '../utils/authActionQuery.mjs';

export const GA4_MEASUREMENT_ID = 'G-79JNFWTLCJ';

export const GA4_CONVERSION_EVENTS = Object.freeze([
  'sign_up',
  'begin_checkout',
  'purchase',
  'generate_metadata',
  'demo_start',
  'demo_complete',
  'demo_error',
  'cta_click'
]);

export const DEMO_ERROR_BUCKETS = Object.freeze([
  'no_file',
  'not_image',
  'too_large',
  'locked_24h',
  'rate_limited_429',
  'too_few_keywords',
  'read_failed',
  'failed'
]);

export const CTA_TARGETS = Object.freeze([
  'studio',
  'login',
  'pricing',
  'free_credits',
  'tool_page',
  'vs',
  'blog',
  'help',
  'external'
]);

const SIGN_UP_METHODS = new Set(['google', 'email', 'phone']);
const ASSET_TYPES = new Set(['image', 'video', 'vector', 'png']);
// ToolLandingPage demo modes (getConfiguredDemoModes): 'default' or 'getty'.
const DEMO_METADATA_MODES = new Set(['default', 'getty']);
const CONTENT_LOCALES = new Set(['en', ...PUBLIC_LOCALE_PREFIXES]);
const GA_EVENT_NAME_SET = new Set(GA4_CONVERSION_EVENTS);
const DEMO_ERROR_BUCKET_SET = new Set(DEMO_ERROR_BUCKETS);
const GENERATE_METADATA_SESSION_KEY = 'stocktag_ga4_generate_metadata_v1';
const PURCHASE_SESSION_KEY_PREFIX = 'stocktag_ga4_purchase_v1:';
const SITE_HOST_PATTERN = /(?:^|\.)stocktag\.ai$/i;
const STUDIO_APP_PATHS = new Set([
  '/metadata',
  '/market-intelligence',
  '/image-generator',
  '/vectorizer',
  '/prompter'
]);
// Single-segment public routes that are not tool landing pages. Any other
// single-segment internal path is a prerendered tool landing page.
const NON_TOOL_ROOT_PATHS = new Set([
  ...STUDIO_APP_PATHS,
  '/pricing',
  '/free-credits',
  '/feedback',
  '/help',
  '/blog',
  '/admin',
  '/about',
  '/editorial-policy',
  '/contact',
  '/privacy',
  '/terms',
  '/pay',
  '/404',
  '/alternatives',
  '/stocktag-vs-alternatives',
  '/stocktag-vs-alternatifleri'
]);
const VS_ROOT_PATHS = new Set([
  '/alternatives',
  '/stocktag-vs-alternatives',
  '/stocktag-vs-alternatifleri'
]);
const LOGIN_AUTH_VALUES = new Set(['login', 'signin', 'signup', 'register']);
const SAFE_TOKEN_PATTERN = /^[a-z0-9_.:-]{1,64}$/i;
// Defense in depth: parameter names that could carry personal data or raw
// URLs are dropped even if a caller passes them by mistake.
const FORBIDDEN_PARAM_KEY_PATTERN = /(?:e_?mail|uid|user|phone|file|message|url|href|query|location|referrer|token|name)/i;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;

const hasWindow = () => typeof window !== 'undefined';

const readSessionFlag = (key) => {
  try {
    return window.sessionStorage?.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeSessionFlag = (key) => {
  try {
    window.sessionStorage?.setItem(key, '1');
  } catch {
    // The event is still sent; only the per-session dedupe is lost.
  }
};

const toSafeToken = (value, fallback = '') => {
  const normalized = String(value ?? '').trim();
  return SAFE_TOKEN_PATTERN.test(normalized) ? normalized : fallback;
};

const toFiniteAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return Number(numeric.toFixed(2));
};

const toQuantity = (value) => {
  const numeric = Number.parseInt(value, 10);
  return Number.isFinite(numeric) && numeric > 0 ? Math.min(numeric, 10000) : 1;
};

const normalizeCurrency = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return CURRENCY_PATTERN.test(normalized) ? normalized : '';
};

const getPathLocale = (pathname = '/') => {
  const firstSegment = String(pathname || '/').split('/').filter(Boolean)[0] || '';
  const normalized = firstSegment.toLowerCase();
  return CONTENT_LOCALES.has(normalized) ? normalized : 'en';
};

export const getContentLocale = () => {
  if (!hasWindow()) return 'en';
  const documentLang = String(
    (typeof document !== 'undefined' && document.documentElement?.lang) || ''
  ).trim().toLowerCase().split(/[-_]/)[0];
  if (CONTENT_LOCALES.has(documentLang)) return documentLang;
  return getPathLocale(window.location?.pathname || '/');
};

export const getRouteTemplate = () => {
  if (!hasWindow()) return '/';
  return getPublicRouteTemplate(window.location?.pathname || '/');
};

export const getCountBucket = (count) => {
  const numeric = Number(count);
  if (!Number.isFinite(numeric) || numeric <= 1) return '1';
  if (numeric <= 5) return '2-5';
  if (numeric <= 20) return '6-20';
  if (numeric <= 100) return '21-100';
  return '101+';
};

export const canSendGaEvent = () => {
  if (!hasWindow()) return false;
  if (typeof window.gtag !== 'function') return false;
  if (window.__stocktagGoogleTagsConfigured !== true) return false;
  if (isAuthActionUrlActive()) return false;
  try {
    return isAnalyticsConsentGranted() === true;
  } catch {
    return false;
  }
};

const sanitizeItems = (items) => (
  (Array.isArray(items) ? items : [])
    .slice(0, 10)
    .map((item) => {
      const itemId = toSafeToken(item?.item_id);
      if (!itemId) return null;
      const price = toFiniteAmount(item?.price);
      return Object.fromEntries(Object.entries({
        item_id: itemId,
        item_category: toSafeToken(item?.item_category) || undefined,
        item_variant: toSafeToken(item?.item_variant) || undefined,
        price: price === null ? undefined : price,
        quantity: toQuantity(item?.quantity)
      }).filter(([, value]) => value !== undefined));
    })
    .filter(Boolean)
);

const sanitizeParams = (params = {}) => {
  const safe = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(key) || FORBIDDEN_PARAM_KEY_PATTERN.test(key)) return;
    if (key === 'items') {
      const items = sanitizeItems(value);
      if (items.length) safe.items = items;
      return;
    }
    if (typeof value === 'number') {
      if (Number.isFinite(value)) safe[key] = value;
      return;
    }
    if (typeof value === 'boolean') {
      safe[key] = value;
      return;
    }
    const token = toSafeToken(value);
    if (token) safe[key] = token;
  });
  return safe;
};

// Sends one GA4 event. Returns true only when the event reached gtag.
// Never throws: callers sit next to auth and checkout code.
export const sendGaEvent = (eventName, params = {}, { beacon = false } = {}) => {
  try {
    if (!GA_EVENT_NAME_SET.has(eventName) || !canSendGaEvent()) return false;
    const payload = {
      route_template: getRouteTemplate(),
      content_locale: getContentLocale(),
      ...sanitizeParams(params),
      send_to: GA4_MEASUREMENT_ID
    };
    if (beacon) payload.transport_type = 'beacon';
    window.gtag('event', eventName, payload);
    return true;
  } catch {
    return false;
  }
};

export const trackGaSignUp = ({ method = '' } = {}) => {
  const normalized = String(method || '').trim().toLowerCase();
  return sendGaEvent('sign_up', {
    method: SIGN_UP_METHODS.has(normalized) ? normalized : 'other'
  });
};

export const trackGaBeginCheckout = ({
  packageId = '',
  packageType = '',
  billingPeriod = '',
  price,
  quantity = 1,
  currency = ''
} = {}) => {
  const safeQuantity = toQuantity(quantity);
  const unitPrice = toFiniteAmount(price);
  const safeCurrency = normalizeCurrency(currency);
  const value = unitPrice === null ? null : Number((unitPrice * safeQuantity).toFixed(2));
  return sendGaEvent('begin_checkout', {
    ...(value !== null && safeCurrency ? { value, currency: safeCurrency } : {}),
    items: [{
      item_id: packageId,
      item_category: packageType,
      item_variant: billingPeriod,
      price: unitPrice,
      quantity: safeQuantity
    }]
  });
};

// Builds the GA4/Meta purchase payload from a `paddle-payment-success`
// detail. Email, user id and queue fingerprints are deliberately dropped.
export const buildPurchaseAnalyticsPayload = (detail = {}) => {
  const transactionId = toSafeToken(detail?.transactionId);
  if (!transactionId) return null;
  const quantity = toQuantity(detail?.quantity);
  // paddleService falls back to 0 when Paddle reports no totals, so a zero
  // amount is treated as unknown rather than reported as free revenue.
  const amount = toFiniteAmount(detail?.price);
  const value = amount !== null && amount > 0 ? amount : null;
  const currency = normalizeCurrency(detail?.currency);
  const credits = Number(detail?.credits);
  return {
    transactionId,
    packageId: toSafeToken(detail?.packageId),
    packageType: toSafeToken(detail?.packageType),
    billingPeriod: toSafeToken(detail?.billingPeriod),
    quantity,
    value: value !== null && currency ? value : null,
    unitPrice: value !== null && currency ? Number((value / quantity).toFixed(2)) : null,
    currency,
    credits: Number.isFinite(credits) && credits > 0 ? Math.round(credits) : null
  };
};

export const trackGaPurchase = (purchase) => {
  if (!purchase?.transactionId) return false;
  const sessionKey = `${PURCHASE_SESSION_KEY_PREFIX}${purchase.transactionId}`;
  if (hasWindow() && readSessionFlag(sessionKey)) return false;
  const sent = sendGaEvent('purchase', {
    transaction_id: purchase.transactionId,
    ...(purchase.value !== null ? { value: purchase.value, currency: purchase.currency } : {}),
    billing_period: purchase.billingPeriod,
    items: [{
      item_id: purchase.packageId || 'purchase',
      item_category: purchase.packageType,
      item_variant: purchase.billingPeriod,
      price: purchase.unitPrice,
      quantity: purchase.quantity
    }]
  });
  if (sent) writeSessionFlag(sessionKey);
  return sent;
};

// One event per browser session: the first successful generation, with the
// asset type of that result and a coarse bucket of the batch it belonged to.
export const trackGaGenerateMetadataOnce = ({ assetType = '', batchSize = 1 } = {}) => {
  if (!hasWindow() || readSessionFlag(GENERATE_METADATA_SESSION_KEY)) return false;
  const normalizedType = String(assetType || '').trim().toLowerCase();
  const sent = sendGaEvent('generate_metadata', {
    asset_type: ASSET_TYPES.has(normalizedType) ? normalizedType : 'other',
    count_bucket: getCountBucket(batchSize)
  });
  if (sent) writeSessionFlag(GENERATE_METADATA_SESSION_KEY);
  return sent;
};

const normalizeDemoMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return DEMO_METADATA_MODES.has(normalized) ? normalized : 'default';
};

export const trackGaDemoStart = ({ metadataMode = '' } = {}) => (
  sendGaEvent('demo_start', { metadata_mode: normalizeDemoMode(metadataMode) })
);

export const trackGaDemoComplete = ({ metadataMode = '' } = {}) => (
  sendGaEvent('demo_complete', { metadata_mode: normalizeDemoMode(metadataMode) })
);

export const trackGaDemoError = ({ errorBucket = 'failed', metadataMode = '' } = {}) => (
  sendGaEvent('demo_error', {
    error_bucket: DEMO_ERROR_BUCKET_SET.has(errorBucket) ? errorBucket : 'failed',
    metadata_mode: normalizeDemoMode(metadataMode)
  })
);

const stripLocalePrefix = (pathname = '/') => {
  const segments = String(pathname || '/').split('/').filter(Boolean);
  if (segments.length && PUBLIC_LOCALE_PREFIXES.includes(segments[0].toLowerCase())) {
    segments.shift();
  }
  return segments.length ? `/${segments.join('/')}` : '/';
};

// Maps a link href to a fixed CTA target. Returns null for links that are not
// conversion paths (home, in-page anchors, legal pages, mailto:, etc.).
export const classifyCtaHref = (href, baseUrl = 'https://www.stocktag.ai/') => {
  const rawHref = String(href || '').trim();
  if (!rawHref || rawHref.startsWith('#') || /^(?:mailto:|tel:|javascript:|blob:|data:)/i.test(rawHref)) {
    return null;
  }

  let url;
  try {
    url = new URL(rawHref, baseUrl);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(url.protocol)) return null;

  let baseHost = '';
  try {
    baseHost = new URL(baseUrl).hostname;
  } catch {
    baseHost = '';
  }
  const isInternal = url.hostname === baseHost || SITE_HOST_PATTERN.test(url.hostname);
  if (!isInternal) return 'external';

  const authValue = String(url.searchParams.get('auth') || '').trim().toLowerCase();
  if (LOGIN_AUTH_VALUES.has(authValue)) return 'login';

  const path = stripLocalePrefix(url.pathname).replace(/\/+$/, '') || '/';
  const lowerPath = path.toLowerCase();
  if (lowerPath === '/pricing') return 'pricing';
  if (lowerPath === '/free-credits') return 'free_credits';
  if (STUDIO_APP_PATHS.has(lowerPath)) return 'studio';
  if (lowerPath === '/blog' || lowerPath.startsWith('/blog/')) return 'blog';
  if (lowerPath.startsWith('/vs/') || VS_ROOT_PATHS.has(lowerPath)) return 'vs';
  if (lowerPath === '/help' || lowerPath.startsWith('/help/') || lowerPath.startsWith('/video/')) return 'help';
  if (/^\/[a-z0-9-]+$/.test(lowerPath) && !NON_TOOL_ROOT_PATHS.has(lowerPath)) return 'tool_page';
  return null;
};

const getCtaSection = (anchor) => {
  const landmark = anchor?.closest?.('header, nav, footer, aside, main');
  const tagName = String(landmark?.tagName || '').toLowerCase();
  return ['header', 'nav', 'footer', 'aside', 'main'].includes(tagName) ? tagName : 'other';
};

const stripPublicLocalePrefix = (pathname = '/') => {
  const segments = String(pathname || '/').split('/').filter(Boolean);
  if (segments.length && PUBLIC_LOCALE_PREFIXES.includes(segments[0])) segments.shift();
  return `/${segments.join('/')}`;
};

export const isSamePageInAnotherLanguage = (href, baseUrl = 'https://www.stocktag.ai/') => {
  try {
    const base = new URL(baseUrl);
    const target = new URL(String(href || ''), base);
    if (target.origin !== base.origin) return false;
    return stripPublicLocalePrefix(target.pathname) === stripPublicLocalePrefix(base.pathname);
  } catch {
    return false;
  }
};

// One delegated, capture-phase listener for the whole document. Home CTAs
// call preventDefault() and then window.location.assign(), and tool-landing
// CTAs are full page loads, so the event is sent with beacon transport.
export const installGaCtaClickListener = () => {
  if (!hasWindow() || typeof document === 'undefined') return () => {};

  const handleClick = (event) => {
    try {
      const anchor = event?.target?.closest?.('a[href]');
      if (!anchor) return;
      // Language switches (the alternates nav, any hreflang link) and links to
      // this same page in another language are navigation, not conversion CTAs.
      if (anchor.hasAttribute?.('hreflang') || anchor.closest?.('.language-alternates')) return;
      if (isSamePageInAnotherLanguage(anchor.getAttribute('href'), window.location.href)) return;
      const ctaTarget = classifyCtaHref(anchor.getAttribute('href'), window.location.href);
      if (!ctaTarget) return;
      sendGaEvent('cta_click', {
        cta_target: ctaTarget,
        cta_section: getCtaSection(anchor)
      }, { beacon: true });
    } catch {
      // Analytics must never interfere with navigation.
    }
  };

  document.addEventListener('click', handleClick, true);
  return () => document.removeEventListener('click', handleClick, true);
};
